import { motion } from "motion/react";
import { Link } from "react-router-dom";
import {
  BarChart3,
  DollarSign,
  Package,
  Users,
  ShoppingCart,
  Truck,
  Globe,
  Database,
  Smartphone,
  ArrowRight,
  CheckCircle2,
  Zap,
  Brain
} from "lucide-react";

export function PlataformaPage() {
  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero */}
      <section className="py-24 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full mb-6">
              <Zap className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700 font-medium">Plataforma Inteligente</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6">
              Gestão empresarial<br />
              <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                completa e inteligente
              </span>
            </h1>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Plataforma modular cloud-native que integra todos os processos da sua empresa em um único ecossistema digital.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Módulos */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Módulos <span className="text-green-600">integrados</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ecosistema completo de gestão empresarial, com módulos integrados e arquitetura preparada para expansão
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ShoppingCart, name: "Comercial", desc: "Vendas, pedidos e relacionamento com clientes", iconWrapperClassName: "w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform", iconClassName: "w-6 h-6 text-blue-600" },
              { icon: DollarSign, name: "Financeiro", desc: "Contas, fluxo de caixa e gestão financeira", iconWrapperClassName: "w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform", iconClassName: "w-6 h-6 text-green-600" },
              { icon: Package, name: "Produção", desc: "Planejamento, controle e otimização produtiva", iconWrapperClassName: "w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform", iconClassName: "w-6 h-6 text-purple-600" },
              { icon: BarChart3, name: "BI & Analytics", desc: "Dashboards e inteligência de negócios", iconWrapperClassName: "w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform", iconClassName: "w-6 h-6 text-indigo-600" },
              { icon: Users, name: "CRM", desc: "Gestão completa do relacionamento com clientes", iconWrapperClassName: "w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform", iconClassName: "w-6 h-6 text-pink-600" },
              { icon: Database, name: "Estoque", desc: "Controle total de inventário e movimentações", iconWrapperClassName: "w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform", iconClassName: "w-6 h-6 text-orange-600" },
              { icon: ShoppingCart, name: "Compras", desc: "Aquisições, fornecedores e cotações", iconWrapperClassName: "w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform", iconClassName: "w-6 h-6 text-teal-600" },
              { icon: Globe, name: "Fiscal", desc: "Notas fiscais, impostos e compliance", iconWrapperClassName: "w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform", iconClassName: "w-6 h-6 text-red-600" },
              { icon: BarChart3, name: "Contabilidade", desc: "Plano de contas e gestão contábil", iconWrapperClassName: "w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform", iconClassName: "w-6 h-6 text-cyan-600" },
              { icon: Truck, name: "Frota", desc: "Logística, rotas e gestão de veículos", iconWrapperClassName: "w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform", iconClassName: "w-6 h-6 text-emerald-600" },
              { icon: Smartphone, name: "Mobile", desc: "Acesso completo via aplicativo móvel", iconWrapperClassName: "w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform", iconClassName: "w-6 h-6 text-violet-600" },
              { icon: Brain, name: "IA & Automação", desc: "Inteligência artificial e automações", iconWrapperClassName: "w-12 h-12 bg-fuchsia-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform", iconClassName: "w-6 h-6 text-fuchsia-600" },
            ].map((module, index) => (
              <motion.div
                key={index}
                className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 cursor-pointer"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -4 }}
              >
                <div className={module.iconWrapperClassName}>
                  <module.icon className={module.iconClassName} />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{module.name}</h4>
                <p className="text-sm text-gray-600">{module.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Multi-empresa<br />e <span className="text-green-600">multi-tenant</span>
              </h2>

              <p className="text-xl text-gray-600 mb-8">
                Gerencie múltiplas empresas em uma única plataforma, com isolamento total de dados e gestão centralizada de licenças.
              </p>

              <div className="space-y-4">
                {[
                  "Isolamento completo de dados por empresa",
                  "Gestão centralizada de usuários e permissões",
                  "Licensing flexível e escalável",
                  "Backoffice administrativo completo"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-3xl shadow-2xl p-8">
                <div className="space-y-4">
                  {["Empresa A", "Empresa B", "Empresa C"].map((empresa, i) => (
                    <motion.div
                      key={i}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-500 rounded-lg"></div>
                        <div>
                          <div className="font-semibold text-gray-900">{empresa}</div>
                          <div className="text-sm text-gray-500">Licença Enterprise</div>
                        </div>
                      </div>
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Pronto para transformar sua gestão?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Agende uma demonstração e veja a plataforma em ação
          </p>
          <Link to="/demonstracao">
            <motion.button
              className="px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:shadow-xl hover:shadow-green-500/30 transition-all flex items-center gap-2 mx-auto font-medium"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Solicitar demonstração
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </Link>
        </div>
      </section>
    </div>
  );
}
